import {
  JobseekerCreateDto,
  JobseekerExperienceCreateDto,
  JobseekerEducationCreateDto,
  JobseekerReferenceCreateDto,
  JobseekerAssociationCreateDto,
  JobseekerCertificateCreateDto,
  JobseekerProfileResponseDto,
  JobseekerProfileUpdateDto,
  JobseekerUpdateDto,
  Pagination,
} from '@libs/dto';
import { Jobseeker, JobseekerExperience, JobseekerEducation, JobseekerReference, JobseekerAssociation, JobseekerCertificate } from '@libs/models';

import { api } from '../common.api';

export const jobseekerApi = api.injectEndpoints({
  endpoints: (build) => ({
    jobseekerCreate: build.mutation<Jobseeker, JobseekerCreateDto>({
      query: (body) => ({
        url: '/jobseeker',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerIndex: build.query<Pagination<Jobseeker>, { page?: number; limit?: number }>({
      query: ({ page = 1, limit = 10 } = {}) => ({
        url: '/jobseeker',
        method: 'GET',
        params: { page, limit },
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerSearch: build.query<Pagination<Jobseeker>, { q: string; page?: number; limit?: number }>({
      query: ({ q, page = 1, limit = 10 }) => ({
        url: '/jobseeker/search',
        method: 'GET',
        params: { q, page, limit },
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerView: build.query<Jobseeker, number>({
      query: (id) => ({
        url: `/jobseeker/${id}`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerUpdate: build.mutation<Jobseeker, JobseekerUpdateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDelete: build.mutation<Jobseeker, number>({
      query: (id) => ({
        url: `/jobseeker/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUser: build.query<Jobseeker, number>({
      query: (id) => ({
        url: `/jobseeker/user/${id}`,
        method: 'GET',
      }),
    }),
    jobseekerProfile: build.query<JobseekerProfileResponseDto, number>({
      query: (id) => ({
        url: `/jobseeker/${id}/profile`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerUpdateProfile: build.mutation<JobseekerProfileResponseDto, JobseekerProfileUpdateDto & { id: number }>({
      query: (body) => {
        const { id, ...profileData } = body;
        return {
          url: `/jobseeker/${id}/profile`,
          method: 'PATCH',
          body: profileData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerExperiences: build.query<JobseekerExperience[], number>({
      query: (id) => ({
        url: `/jobseeker/${id}/experience`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerAddExperience: build.mutation<JobseekerExperience, JobseekerExperienceCreateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/experience`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUpdateExperience: build.mutation<JobseekerExperience, JobseekerExperienceCreateDto & { id: number }>({
      query: (body) => {
        const { id, jobseekerId, ...experienceData } = body;
        return {
          url: `/jobseeker/${jobseekerId}/experience/${id}`,
          method: 'PATCH',
          body: experienceData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDeleteExperience: build.mutation<JobseekerExperience, { jobseekerId: number; experienceId: number }>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/experience/${body.experienceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerEducations: build.query<JobseekerEducation[], number>({
      query: (id) => ({
        url: `/jobseeker/${id}/education`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerAddEducation: build.mutation<JobseekerEducation, JobseekerEducationCreateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/education`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUpdateEducation: build.mutation<JobseekerEducation, JobseekerEducationCreateDto & { id: number }>({
      query: (body) => {
        const { id, jobseekerId, ...educationData } = body;
        return {
          url: `/jobseeker/${jobseekerId}/education/${id}`,
          method: 'PATCH',
          body: educationData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDeleteEducation: build.mutation<JobseekerEducation, { jobseekerId: number; educationId: number }>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/education/${body.educationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerReferences: build.query<JobseekerReference[], number>({
      query: (id) => ({
        url: `/jobseeker/${id}/references`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerAddReference: build.mutation<JobseekerReference, JobseekerReferenceCreateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/references`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUpdateReference: build.mutation<JobseekerReference, JobseekerReferenceCreateDto & { id: number }>({
      query: (body) => {
        const { id, jobseekerId, ...referenceData } = body;
        return {
          url: `/jobseeker/${jobseekerId}/references/${id}`,
          method: 'PATCH',
          body: referenceData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDeleteReference: build.mutation<JobseekerReference, { jobseekerId: number; referenceId: number }>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/references/${body.referenceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerAssociations: build.query<JobseekerAssociation[], number>({
      query: (id) => ({
        url: `/jobseeker/${id}/associations`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerAddAssociation: build.mutation<JobseekerAssociation, JobseekerAssociationCreateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/associations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUpdateAssociation: build.mutation<JobseekerAssociation, JobseekerAssociationCreateDto & { id: number }>({
      query: (body) => {
        const { id, jobseekerId, ...associationData } = body;
        return {
          url: `/jobseeker/${jobseekerId}/associations/${id}`,
          method: 'PATCH',
          body: associationData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDeleteAssociation: build.mutation<JobseekerAssociation, { jobseekerId: number; associationId: number }>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/associations/${body.associationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerCertificates: build.query<JobseekerCertificate[], number>({
      query: (id) => ({
        url: `/jobseeker/${id}/certificates`,
        method: 'GET',
      }),
      providesTags: ['Jobseeker'],
    }),
    jobseekerAddCertificate: build.mutation<JobseekerCertificate, JobseekerCertificateCreateDto>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/certificates`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerUpdateCertificate: build.mutation<JobseekerCertificate, JobseekerCertificateCreateDto & { id: number }>({
      query: (body) => {
        const { id, jobseekerId, ...certificateData } = body;
        return {
          url: `/jobseeker/${jobseekerId}/certificates/${id}`,
          method: 'PATCH',
          body: certificateData,
        };
      },
      invalidatesTags: ['Jobseeker'],
    }),
    jobseekerDeleteCertificate: build.mutation<JobseekerCertificate, { jobseekerId: number; certificateId: number }>({
      query: (body) => ({
        url: `/jobseeker/${body.jobseekerId}/certificates/${body.certificateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Jobseeker'],
    }),
  }),
});

export const {
  useJobseekerCreateMutation,
  useJobseekerIndexQuery,
  useJobseekerSearchQuery,
  useJobseekerViewQuery,
  useJobseekerUpdateMutation,
  useJobseekerDeleteMutation,
  useJobseekerProfileQuery,
  useJobseekerUpdateProfileMutation,
  useJobseekerUserQuery,
  useJobseekerExperiencesQuery,
  useJobseekerAddExperienceMutation,
  useJobseekerUpdateExperienceMutation,
  useJobseekerDeleteExperienceMutation,
  useJobseekerEducationsQuery,
  useJobseekerAddEducationMutation,
  useJobseekerUpdateEducationMutation,
  useJobseekerDeleteEducationMutation,
  useJobseekerReferencesQuery,
  useJobseekerAddReferenceMutation,
  useJobseekerUpdateReferenceMutation,
  useJobseekerDeleteReferenceMutation,
  useJobseekerAssociationsQuery,
  useJobseekerAddAssociationMutation,
  useJobseekerUpdateAssociationMutation,
  useJobseekerDeleteAssociationMutation,
  useJobseekerCertificatesQuery,
  useJobseekerAddCertificateMutation,
  useJobseekerUpdateCertificateMutation,
  useJobseekerDeleteCertificateMutation,
} = jobseekerApi;
